<script>
import { loadLocale } from 'dashboard/i18n';
import { markRaw, provide, ref } from 'vue';
import { mapGetters } from 'vuex';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import BrowserNotificationPrompt from './components/BrowserNotificationPrompt.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import PendingEmailVerificationBanner from './components/app/PendingEmailVerificationBanner.vue';
import vueActionCable from './helper/actionCable';
import { useRouter } from 'vue-router';
import { useStore } from 'dashboard/composables/store';
import { frontendURL } from 'dashboard/helper/URLHelper';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import { setColorTheme } from './helper/themeHelper';
import { isOnOnboardingView } from 'v3/helpers/RouteHelper';
import { useAccount } from 'dashboard/composables/useAccount';
import { useFontSize } from 'dashboard/composables/useFontSize';
import { useNotificationFavicon } from 'dashboard/composables/useNotificationFavicon';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';
import ReconnectService from 'dashboard/helper/ReconnectService';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { setApiClientAccountId } from 'dashboard/api/ApiClient';
import MobilePageSkeleton from './routes/mobile/MobilePageSkeleton.vue';
import { useMarketingConsent } from 'v3/composables/useMarketingConsent';

export default {
  name: 'App',

  components: {
    LoadingState,
    MobilePageSkeleton,
    NetworkNotification,
    BrowserNotificationPrompt,
    PaymentPendingBanner,
    WootSnackbarBox,
    PendingEmailVerificationBanner,
  },
  setup() {
    useMarketingConsent({ authenticated: true });
    const mobileReconnecting = ref(false);
    provide('mobileReconnecting', mobileReconnecting);
    const router = useRouter();
    const store = useStore();
    const { accountId } = useAccount();
    // Use the font size composable (it automatically sets up the watcher)
    const { currentFontSize } = useFontSize();
    const { uiSettings } = useUISettings();
    useNotificationFavicon();

    return {
      mobileReconnecting,
      router,
      store,
      currentAccountId: accountId,
      currentFontSize,
      uiSettings,
    };
  },
  data() {
    return {
      reconnectService: null,
      cableConnector: null,
      pushRegistrationFailed: false,
      activeLocale: window.chatwootConfig.selectedLocale || 'en',
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      isRTL: 'accounts/isRTL',
      currentUser: 'getCurrentUser',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
    }),
    hideOnOnboardingView() {
      return !isOnOnboardingView(this.$route);
    },
    isMobileView() {
      return this.$route.meta.mobile;
    },
    localCaptureClass() {
      // Render native UI text at capture resolution in this local demo only.
      if (!import.meta.env.DEV || Number(this.currentAccountId) !== 3281) {
        return '';
      }
      if (this.$route.query.capture === 'retina') {
        return 'origin-top-left !w-1/2 !h-[50vh] scale-[2]';
      }
      return this.$route.query.capture === 'overview'
        ? 'origin-top-left !w-4/5 !h-[80vh] scale-[1.25]'
        : '';
    },
  },

  watch: {
    currentAccountId: {
      immediate: true,
      handler(accountId) {
        setApiClientAccountId(accountId);
        if (accountId) {
          this.initializeAccount();
        }
      },
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    // User UI language (profile); otherwise server default or English
    this.setLocale(
      this.uiSettings?.locale || window.chatwootConfig.selectedLocale || 'en'
    );
  },
  unmounted() {
    this.cableConnector?.disconnect();
    if (this.reconnectService) {
      this.reconnectService.disconnect();
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    async setLocale(locale) {
      if (locale) {
        this.$root.$i18n.locale = await loadLocale(
          this.$root.$i18n,
          locale
        ).catch(() => 'en');
        this.activeLocale = locale;
      }
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const account = this.getAccount(this.currentAccountId);

      if (this.shouldRedirectToSignupOnboarding(account)) {
        window.location.assign(
          frontendURL('auth/onboarding', {
            account_id: this.currentAccountId,
          })
        );
        return;
      }

      const { pubsub_token: pubsubToken } = this.currentUser || {};
      this.setLocale(
        this.uiSettings?.locale || window.chatwootConfig.selectedLocale || 'en'
      );
      this.cableConnector?.disconnect();
      this.reconnectService?.disconnect();
      this.reconnectService = markRaw(
        new ReconnectService(this.store, this.router)
      );
      this.cableConnector = markRaw(
        vueActionCable.init(this.store, pubsubToken)
      );
      window.reconnectService = this.reconnectService;

      if (!this.isMobileView) {
        this.pushRegistrationFailed = false;
        verifyServiceWorkerExistence(registration =>
          registration.pushManager.getSubscription().then(subscription => {
            if (subscription) {
              const onError =
                window.innerWidth >= 768
                  ? () => {
                      this.pushRegistrationFailed = true;
                    }
                  : null;
              registerSubscription(undefined, onError);
            }
          })
        );
      }
    },
    shouldRedirectToSignupOnboarding(account = {}) {
      const customAttributes = account.custom_attributes || {};
      return (
        customAttributes.signup_onboarding_step &&
        customAttributes.onboarding_step !== 'completed'
      );
    },
  },
};
</script>

<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="fixed inset-0 flex min-h-0 w-full flex-col overflow-hidden bg-n-background"
    :class="[isMobileView ? 'h-[100dvh]' : 'h-screen', localCaptureClass]"
    :dir="isRTL ? 'rtl' : 'ltr'"
    :lang="activeLocale"
  >
    <template v-if="currentAccountId">
      <PendingEmailVerificationBanner v-if="hideOnOnboardingView" />
      <PaymentPendingBanner v-if="hideOnOnboardingView" />
      <BrowserNotificationPrompt
        v-if="hideOnOnboardingView && !isMobileView"
        :registration-failed="pushRegistrationFailed"
      />
    </template>
    <router-view v-slot="{ Component }">
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <WootSnackbarBox />
    <NetworkNotification @reconnecting="mobileReconnecting = $event" />
  </div>
  <MobilePageSkeleton v-else-if="isMobileView" />
  <LoadingState v-else />
</template>

<style lang="scss">
@import './assets/scss/app';

.v-popper--theme-tooltip .v-popper__inner {
  background: black !important;
  font-size: 0.75rem;
  padding: 4px 8px !important;
  border-radius: 6px;
  font-weight: 400;
  white-space: pre-line;
}

.v-popper--theme-tooltip .v-popper__arrow-container {
  display: none;
}
</style>
